import { Alert, Fade } from '@mui/material';
import React, { ChangeEvent, FC, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import renameDashboardFetcher from '../../../api/dashboard/renameDashboard';
import updateDashboardColumnsLayoutFetcher from '../../../api/dashboard/updateDashboardColumnsLayout';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors } from '../../../theme';
import { fontSize, fontWeight, TypographyStyledGlobal } from '../../../theme/font';
import useFetcher from '../../../utils/hooks/useFetcher';
import { DashboardFromApi, DashboardParams } from '../../Dashboard/dashboardInterfaces';
import Title from '../../Title/title';
import ColumnTemplateSelector from '../AddDashboardModal/columnTemplateSelector';
import { CloseIconModalStyled, InputStyled, ModalStyled } from '../AddDashboardModal/styled';
import BoxModalStyled, { ErrorInputMessageStyled } from '../styled';
import ActionsButton from './ActionButton';
import { DeleteDashboardProps } from './ConfirmationModalInterface';

const DEFAULT_COLUMNS_CONFIG = [33, 33, 34];

const RenameDashboardModal: FC<DeleteDashboardProps> = ({ openModal, setOpenModal, title }) => {
    const { t } = useTranslation(['common', 'dashboard']);
    const { surveyId, dashboardId } = useParams<DashboardParams>();
    const { mutate, cache } = useSWRConfig();
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const fetcher = useFetcher();

    const dashboardUrl = `${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard/${dashboardId}`;
    const { data: fetchedDashboard } = useSWR<DashboardFromApi>(openModal ? dashboardUrl : null, fetcher);

    const currentColumnsConfig = fetchedDashboard?.columnsConfig ?? DEFAULT_COLUMNS_CONFIG;
    const [selectedColumnsConfig, setSelectedColumnsConfig] = useState<number[]>(currentColumnsConfig);
    const [renameValue, setRenameValue] = useState<string>(title);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        if (fetchedDashboard) {
            setSelectedColumnsConfig(fetchedDashboard.columnsConfig ?? DEFAULT_COLUMNS_CONFIG);
        }
    }, [fetchedDashboard]);

    useEffect(() => {
        if (openModal) {
            setRenameValue(title);
            setErrorMessage('');
        }
    }, [openModal, title]);

    const handleClose = () => {
        setOpenModal(false);
    };

    const handleClickConfirm = async () => {
        if (!renameValue) {
            setErrorMessage(t('dashboard:renameDashboardModal.errorInputMessage'));

            return;
        }

        await renameDashboardFetcher(
            surveyId || '',
            dashboardId || '',
            { customTitle: renameValue },
            t('dashboard:renameDashboardModal.success', { dashboardTitle: title }),
            t('dashboard:renameDashboardModal.error'),
            selectedSchema || '',
        );

        if (JSON.stringify(selectedColumnsConfig) !== JSON.stringify(currentColumnsConfig)) {
            await updateDashboardColumnsLayoutFetcher(
                surveyId || '',
                dashboardId || '',
                selectedColumnsConfig,
                t('dashboard:renameDashboardModal.successColumnsLayout'),
                t('dashboard:renameDashboardModal.errorColumnsLayout'),
                selectedSchema || '',
            );
        }

        setOpenModal(false);
        mutate(`${process.env.REACT_APP_API_URL}/survey/${surveyId}/dashboard`);
        mutate(dashboardUrl);
        Array.from((cache as unknown as Map<string, unknown>).keys())
            .filter((key) => key.startsWith(`${dashboardUrl}/indicator`))
            .forEach((key) => mutate(key));
    };

    return (
        <ModalStyled
            aria-describedby="modal-modal-description"
            aria-labelledby="modal-modal-title"
            onClose={handleClose}
            open={openModal}
        >
            <Fade in={openModal}>
                <BoxModalStyled>
                    <Title content={t('dashboard:renameDashboardModal.update', { dashboardTitle: title }) as string} modalTitle />
                    <TypographyStyledGlobal
                        color={colors.grey200}
                        margin='8px 0'
                        size={fontSize.m}
                        weight={fontWeight.regular}
                    >
                        {t('dashboard:renameDashboardModal.description')}
                    </TypographyStyledGlobal>
                    <CloseIconModalStyled onClick={handleClose} />
                    <InputStyled
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setRenameValue(e.target.value)}
                        placeholder={t('dashboard:addDashboard.placeholder')}
                        value={renameValue}
                    />
                    {errorMessage && (
                        <ErrorInputMessageStyled>{errorMessage}</ErrorInputMessageStyled>
                    )}
                    <ColumnTemplateSelector
                        onSelect={setSelectedColumnsConfig}
                        selectedConfig={selectedColumnsConfig}
                    />
                    {JSON.stringify(selectedColumnsConfig) !== JSON.stringify(currentColumnsConfig) && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {t('dashboard:renameDashboardModal.warningColumnsLayout')}
                        </Alert>
                    )}
                    <ActionsButton
                        handleClickCancel={handleClose}
                        handleClickConfirm={handleClickConfirm}
                        labelCancel={t('common:cancel')}
                        labelConfirm={t('common:confirm')}
                    />
                </BoxModalStyled>
            </Fade>
        </ModalStyled>
    );
};

export default RenameDashboardModal;
